import React from "react";
import {
    Alert,
    Badge,
    Box,
    Button,
    Columns,
    Column,
    Inline,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Text,
    TextField,
    TooltipRenderer,
    IconTick,
    IconRefresh,
    IconVideo,
} from "braid-design-system";
import { useTranslation } from "react-i18next";
import { useToast } from "../hooks/useToast";
import { useCanManageMasters } from "../hooks/useCanManageMasters";
import { getAllPendingResources, approveItemResource, ItemResourceResponse } from "../services/itemResourceService";
import Loading from "../components/Loading";
import { toStaticResourceUrl } from "../utils/resourceUrl";

const ItemResourceApprovalPanel: React.FC = () => {
    const { t } = useTranslation();
    const toast = useToast();
    const canManageMasters = useCanManageMasters();
    const [loading, setLoading] = React.useState(false);
    const [loadingMessage, setLoadingMessage] = React.useState<string | null>(null);
    const [resources, setResources] = React.useState<ItemResourceResponse[]>([]);
    const [searchTerm, setSearchTerm] = React.useState("");
    const [previewPath, setPreviewPath] = React.useState<string | null>(null);
    const [previewType, setPreviewType] = React.useState<"image" | "video" | null>(null);

    const fetchPendingResources = React.useCallback(async () => {
        try {
            setLoading(true);
            setLoadingMessage("Loading pending resources...");
            const data = await getAllPendingResources();
            setResources(data);
        } catch (error: any) {
            toast.showError(`Error fetching pending resources: ${error?.message || "Unknown error"}`);
        } finally {
            setLoading(false);
            setLoadingMessage(null);
        }
    }, [toast]);

    React.useEffect(() => {
        if (canManageMasters) {
            fetchPendingResources();
        }
    }, [canManageMasters, fetchPendingResources]);

    const handleApprove = React.useCallback(async (resource: ItemResourceResponse) => {
        if (!resource.resource_id) return;

        try {
            await approveItemResource(resource.resource_id);
            toast.showSuccess(`Resource ${resource.resource_id} approved successfully.`);
            setResources((prev) => prev.filter((r) => r.resource_id !== resource.resource_id));
        } catch (error: any) {
            toast.showError(`Error approving resource: ${error?.message || "Unknown error"}`);
        }
    }, [toast]);

    const filteredResources = React.useMemo(() => {
        if (!searchTerm.trim()) {
            return resources;
        }
        const term = searchTerm.trim().toLowerCase();
        return resources.filter((r) => {
            return (
                (r.resource_itemlots_code || "").toLowerCase().includes(term) ||
                (r.resource_type || "").toLowerCase().includes(term) ||
                (r.resource_title || "").toLowerCase().includes(term) ||
                String(r.resource_id ?? "").includes(term)
            );
        });
    }, [resources, searchTerm]);

    if (!canManageMasters) {
        return (
            <Box padding="large">
                <Alert tone="critical">
                    <Text>You do not have permission to access this page. Only administrators can manage resource approvals.</Text>
                </Alert>
            </Box>
        );
    }

    return (
        <>
            {loading && <Loading message={loadingMessage || "Loading..."} />}

            <Box padding="medium">
                <Stack space="large">
                    <Columns space="large" collapseBelow="tablet" alignY="center">
                        <Column width="1/3">
                            <Box display="flex" justifyContent="flexStart" alignItems="center">
                                <Inline space="small" alignY="center">
                                    <Text weight="strong" size="large">
                                        {t("page.item_resource_approval_panel.title", { defaultValue: "Pending Item Resources" })}
                                    </Text>
                                    <Badge tone={resources.length > 0 ? "caution" : "positive"}>
                                        {`${resources.length} pending`}
                                    </Badge>
                                </Inline>
                            </Box>
                        </Column>
                        <Column width="2/3">
                            <Box display="flex" justifyContent="flexEnd">
                                <Inline space="small" alignY="center">
                                    {resources.length > 0 ? (
                                        <TextField
                                            placeholder={t("page.item_resource_approval_panel.search.placeholder", {
                                                defaultValue: "Search pending resources...",
                                            })}
                                            aria-label={t("page.item_resource_approval_panel.search.label", { defaultValue: "Search" })}
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.currentTarget.value)}
                                        />
                                    ) : null}
                                    <Button
                                        variant="ghost"
                                        tone="brandAccent"
                                        icon={<IconRefresh />}
                                        onClick={fetchPendingResources}
                                    >
                                        {t("page.item_resource_approval_panel.button.refresh", { defaultValue: "Refresh" })}
                                    </Button>
                                </Inline>
                            </Box>
                        </Column>
                    </Columns>

                    {!loading && resources.length === 0 ? (
                        <Alert tone="positive">
                            <Text>
                                {t("page.item_resource_approval_panel.empty", {
                                    defaultValue: "There are no pending item resources awaiting approval.",
                                })}
                            </Text>
                        </Alert>
                    ) : (
                        <>
                            <Table
                                label={t("page.item_resource_approval_panel.table.label", {
                                    defaultValue: "Pending Item Resources",
                                })}
                            >
                                <TableHeader>
                                    <TableRow>
                                        <TableHeaderCell>
                                            <Text>{t("page.item_resource_approval_panel.table.headers.resource_id", { defaultValue: "Resource ID" })}</Text>
                                        </TableHeaderCell>
                                        <TableHeaderCell>
                                            <Text>{t("page.item_resource_approval_panel.table.headers.item_code", { defaultValue: "Item Code" })}</Text>
                                        </TableHeaderCell>
                                        <TableHeaderCell>
                                            <Text>{t("page.item_resource_approval_panel.table.headers.type", { defaultValue: "Type" })}</Text>
                                        </TableHeaderCell>
                                        <TableHeaderCell>
                                            <Text>{t("page.item_resource_approval_panel.table.headers.preview", { defaultValue: "Preview" })}</Text>
                                        </TableHeaderCell>
                                        <TableHeaderCell align="right">
                                            <Text>{t("page.item_resource_approval_panel.table.headers.actions", { defaultValue: "Actions" })}</Text>
                                        </TableHeaderCell>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredResources.length > 0 ? (
                                        filteredResources.map((resource) => {
                                            const previewUrl = toStaticResourceUrl(resource.resource_path);
                                            return (
                                                <TableRow key={resource.resource_id}>
                                                    <TableCell>
                                                        <Text>{resource.resource_id ?? "-"}</Text>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Text>{resource.resource_itemlots_code || "-"}</Text>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge tone={resource.resource_type === "video" ? "caution" : "neutral"}>
                                                            {resource.resource_type === "image" ? "Image" : resource.resource_type === "video" ? "Video" : "-"}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        {previewUrl ? (
                                                            resource.resource_type === "image" ? (
                                                                <Box
                                                                    background="neutralLight"
                                                                    padding="xsmall"
                                                                    borderRadius="standard"
                                                                    style={{ maxWidth: 120, maxHeight: 90, overflow: "hidden", cursor: "pointer" }}
                                                                    onClick={() => { setPreviewPath(previewUrl); setPreviewType("image"); }}
                                                                >
                                                                    <img
                                                                        src={previewUrl}
                                                                        alt={resource.resource_title || "preview"}
                                                                        style={{ width: "100%", height: "auto", display: "block" }}
                                                                    />
                                                                </Box>
                                                            ) : (
                                                                <Box
                                                                    position="relative"
                                                                    background="neutralLight"
                                                                    borderRadius="standard"
                                                                    style={{ width: 120, height: 90, overflow: "hidden", cursor: "pointer" }}
                                                                    onClick={() => { setPreviewPath(previewUrl); setPreviewType("video"); }}
                                                                >
                                                                    <video
                                                                        src={`${previewUrl}#t=0.1`}
                                                                        muted
                                                                        preload="metadata"
                                                                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                                                                    />
                                                                    <Box
                                                                        position="absolute"
                                                                        style={{
                                                                            top: "50%",
                                                                            left: "50%",
                                                                            transform: "translate(-50%, -50%)",
                                                                            background: "rgba(255,255,255,0.85)",
                                                                            borderRadius: "50%",
                                                                            width: 28,
                                                                            height: 28,
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            justifyContent: "center",
                                                                            pointerEvents: "none",
                                                                        }}
                                                                    >
                                                                        <IconVideo tone="neutral" size="small" />
                                                                    </Box>
                                                                </Box>
                                                            )
                                                        ) : (
                                                            <Text tone="secondary">-</Text>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Inline space="small">
                                                            <TooltipRenderer
                                                                tooltip={<Text>{t("page.item_resource_approval_panel.tooltip.approve", { defaultValue: "Approve" })}</Text>}
                                                            >
                                                                {({ triggerProps }) => (
                                                                    <Box
                                                                        onClick={() => handleApprove(resource)}
                                                                        cursor="pointer"
                                                                        {...triggerProps}
                                                                    >
                                                                        <IconTick tone="positive" />
                                                                    </Box>
                                                                )}
                                                            </TooltipRenderer>
                                                        </Inline>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    ) : (
                                        <TableRow>
                                            <TableCell align="center" colspan={5}>
                                                <Text tone="secondary">
                                                    {searchTerm
                                                        ? t("page.item_resource_approval_panel.no_results", { defaultValue: "No matching resources found." })
                                                        : t("page.item_resource_approval_panel.empty", { defaultValue: "No pending resources." })}
                                                </Text>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </>
                    )}
                </Stack>
            </Box>

            {previewPath ? (
                <Box
                    position="fixed"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    background="surface"
                    style={{ zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}
                    onClick={() => { setPreviewPath(null); setPreviewType(null); }}
                >
                    <Box
                        background="surface"
                        padding="medium"
                        borderRadius="standard"
                        style={{ maxWidth: "90vw", maxHeight: "90vh", overflow: "hidden" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {previewType === "video" ? (
                            <video controls autoPlay style={{ maxWidth: "85vw", maxHeight: "80vh" }}>
                                <source src={previewPath} />
                            </video>
                        ) : (
                            <img src={previewPath} alt="preview" style={{ maxWidth: "85vw", maxHeight: "80vh" }} />
                        )}
                        <Box marginTop="small" display="flex" justifyContent="flexEnd">
                            <Button variant="ghost" onClick={() => { setPreviewPath(null); setPreviewType(null); }}>
                                Close
                            </Button>
                        </Box>
                    </Box>
                </Box>
            ) : null}
        </>
    );
};

export default ItemResourceApprovalPanel;